/**
 * Normalisation of whatever the storage APIs throw into the two error types the
 * UI knows how to talk about.
 */
import { QuotaExceededError, StorageUnavailableError } from './types';

function errorName(value: unknown): string {
  if (typeof value === 'object' && value !== null && 'name' in value) {
    const { name } = value as { name: unknown };
    if (typeof name === 'string') return name;
  }
  return '';
}

/**
 * True for an out-of-space rejection.
 *
 * `QuotaExceededError` is the modern name; Firefox has historically used
 * `NS_ERROR_DOM_QUOTA_REACHED` and older WebKit used code 22 with a different
 * name, so match on all three rather than on `instanceof DOMException`.
 */
export function isQuotaError(value: unknown): boolean {
  const name = errorName(value);
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') return true;
  if (typeof value === 'object' && value !== null && 'code' in value) {
    const { code } = value as { code: unknown };
    if (code === 22 || code === 1014) return true;
  }
  return false;
}

/**
 * Maps a caught value onto one of our error types, preserving the original as
 * `cause` so the console still shows the real stack.
 */
export function asStorageError(value: unknown, operation: string): Error {
  if (value instanceof QuotaExceededError || value instanceof StorageUnavailableError) {
    return value;
  }
  if (isQuotaError(value)) {
    return new QuotaExceededError(
      `Storage quota exceeded while running "${operation}". Delete or export a project to free space.`,
      { cause: value },
    );
  }
  const detail = value instanceof Error ? value.message : String(value);
  return new StorageUnavailableError(`Storage operation "${operation}" failed: ${detail}`, {
    cause: value,
  });
}
